/* RESPONSIBLE TEAM: team-standalone */
import Service, { inject as service } from '@ember/service';
import { taskFor } from 'ember-concurrency-ts';
import { restartableTask } from 'ember-concurrency-decorators';
import { tracked } from '@glimmer/tracking';
import { post } from 'embercom/lib/ajax';
import {
  IntegrationState,
  StandalonePlatform,
  IntegrationType,
} from 'embercom/objects/standalone/constants';
import type ApplicationInstance from '@ember/application/instance';
import { type TaskGenerator } from 'ember-concurrency';
import { action } from '@ember/object';
import { registerDestructor } from '@ember/destroyable';
import type ReactTeammateAppIslandService from 'embercom/services/react-teammate-app-island';

export type IntegrationNavItems = Partial<Record<IntegrationType, IntegrationNavItem>>;
export type PlatformNavItems = Partial<Record<StandalonePlatform, IntegrationNavItems>>;
export type IntegrationStates = Partial<Record<IntegrationType, IntegrationState>>;

export interface IntegrationNavItem {
  route: string;
  urlSegments: Record<string, string>;
  labelIntlKey: string;
  activeRoutes?: string[];
  requiredFeatures?: string[];
}

export interface IntegrationStatusResponse {
  integration_type: IntegrationType;
  integration_state: IntegrationState;
  // other fields omitted because they're unused
}

const NAV_ITEMS_BY_PLATFORM: PlatformNavItems = {
  [StandalonePlatform.Freshdesk]: {
    [IntegrationType.FreshdeskTickets]: {
      route: 'apps.app.standalone.deploy.channel',
      activeRoutes: ['apps.app.standalone.deploy.channel.freshdesk.tickets'],
      urlSegments: { platform: 'freshdesk', channel: 'tickets' },
      labelIntlKey: 'standalone.navbar.freshdesk.tickets',
    },
    [IntegrationType.Freshchat]: {
      route: 'apps.app.standalone.deploy.channel',
      activeRoutes: ['apps.app.standalone.deploy.channel.freshdesk.messaging'],
      urlSegments: { platform: 'freshdesk', channel: 'messaging' },
      labelIntlKey: 'standalone.navbar.freshdesk.messaging',
      requiredFeatures: ['team-standalone-freshdesk-messaging'],
    },
  },
  [StandalonePlatform.Hubspot]: {
    [IntegrationType.HubspotTickets]: {
      route: 'apps.app.standalone.deploy.channel',
      activeRoutes: ['apps.app.standalone.deploy.channel.hubspot.tickets'],
      urlSegments: { platform: 'hubspot', channel: 'tickets' },
      labelIntlKey: 'standalone.navbar.hubspot.tickets',
    },
    [IntegrationType.HubspotMessenger]: {
      route: 'apps.app.standalone.deploy.channel',
      activeRoutes: ['apps.app.standalone.deploy.channel.hubspot.messaging'],
      urlSegments: { platform: 'hubspot', channel: 'messaging' },
      labelIntlKey: 'standalone.navbar.hubspot.messaging',
    },
  },
  // not implemented until integration has been added in backend
  // [StandalonePlatform.Zendesk],
  // [StandalonePlatform.Salesforce],
  // [StandalonePlatform.Gorgias],
  // [StandalonePlatform.ZohoDesk],
  // [StandalonePlatform.HelpScout],
  // [StandalonePlatform.ServiceNow],
  // [StandalonePlatform.AmazonConnect],
  // [StandalonePlatform.Front],
};

export default class FinStandaloneIntegrationsService extends Service {
  @service declare appService: { app: $TSFixMe };
  @service declare reactTeammateAppIsland: ReactTeammateAppIslandService;
  @tracked private integrationStates: IntegrationStates = {};

  constructor(owner: ApplicationInstance) {
    super(owner);
    taskFor(this.loadIntegrationStatesTask).perform();
    // Subscribe to standalone-deploy-integrations-updated event from react deploy area
    let unsubscribe = this.reactTeammateAppIsland.subscribeToServerStateChange(
      'standalone-deploy-integrations-updated',
      () => {
        taskFor(this.loadIntegrationStatesTask).perform();
      },
    );

    // Register destructor to clean up subscription
    registerDestructor(this, unsubscribe);
  }

  @action
  getIsLiveFn(integrationType: IntegrationType): () => boolean {
    return () => this.integrationStates[integrationType] === IntegrationState.Enabled;
  }

  get platform(): StandalonePlatform {
    return this.appService.app.standalone_platforms?.[0].platform_type;
  }

  get platformIntegrationTypes(): IntegrationType[] {
    let items = NAV_ITEMS_BY_PLATFORM[this.platform] ?? {};

    return Object.entries(items)
      .filter(([, value]) => value !== undefined)
      .map(([key]) => key as unknown as IntegrationType);
  }

  get navItems() {
    let itemsForPlatform = NAV_ITEMS_BY_PLATFORM[this.platform];
    if (!itemsForPlatform) {
      return [];
    }

    return this.platformIntegrationTypes
      .map((integrationType) => {
        let baseItem = itemsForPlatform?.[integrationType];
        if (!baseItem) {
          return undefined;
        }
        return {
          ...baseItem,
          isLive: this.getIsLiveFn(integrationType),
        };
      })
      .filter(Boolean);
  }

  @restartableTask *loadIntegrationStatesTask(): TaskGenerator<void> {
    let response: IntegrationStatusResponse[] = yield post('/ember/standalone/integrations', {
      app_id: this.appService.app.id,
      integration_types: this.platformIntegrationTypes,
    });

    let updatedStates: IntegrationStates = {};
    for (let { integration_type, integration_state } of response) {
      updatedStates[integration_type] = integration_state;
    }

    this.integrationStates = updatedStates;
  }
}
